import { Fragment, useCallback } from 'react';
import { View } from 'react-native';
import StyleSheet from 'react-native-media-query';
import { Divider, Layout, Text, useTheme } from '@ui-kitten/components';
import lodashFind from 'lodash/find';

import ListItem from '../../Components/ListItem';
import PaymentMethods from '../../Components/PaymentMethods';
import CartSummary from '../../Components/StoreCart/CartSummary';
import constants from '../../Config/constants';
import { getCartTerm } from '../../Helper/RemoteConfig';
import useMealPlan from '../../Hooks/useMealPlan';
import { checkout } from '../../RTK/defaultValues';
import themeStyle, { globalSpacing } from '../../Theme/styles';
import breakpoints from '../../Theme/styles/breakpoints';
import {
  ids as platformStyleIds,
  styles as platformStyles,
} from '../../Theme/styles/platform';

import Accordion from './Accordion';
import CheckoutItems from './CheckoutItems';
import ContinueToCheckoutButton from './ContinueToCheckoutButton';
import DeliverySpeed from './DeliverySpeed';
import PaymentOptions from './PaymentOptions';
import PromoCode from './PromoCode';
import useCheckoutHandler from './controller/useCheckoutHandler';

type Props = {
  deliveryQuotation?: {
    codFreeDel: boolean;
    error: string;
    fee?: number;
    loading: boolean;
    mode: string;
    partner?: string;
    priority_fee: number;
    vehicle?: string;
  };
  isPlacingOrder: boolean;
  mealPlanDiscount?: string;
  onBackToStore: () => void;
  onPlaceOrder: () => void;
};

const pickupOrderType = lodashFind(constants.ORDER_TYPE_DATA, {
  value: constants.ORDER_TYPES.PICKUP,
});

const CheckoutRightContent = ({
  deliveryQuotation,
  isPlacingOrder,
  mealPlanDiscount,
  onBackToStore,
  onPlaceOrder,
}: Props) => {
  const theme = useTheme();
  const { isMealPlan } = useMealPlan();
  const {
    additionalCharge,
    checkoutData,
    checkoutInfo,
    checkoutItems,
    formDeliverySpeed,
    formPaymentOptions,
    isDelivery,
    onUpdateCheckoutDetails,
  } = useCheckoutHandler();

  const _onPaymentOptionChanged = useCallback(
    (value) => {
      onUpdateCheckoutDetails(checkout.keys.PAYMENT_OPTIONS, value);
    },
    [onUpdateCheckoutDetails],
  );

  const _onDeliverySpeedChanged = useCallback(
    (value) => {
      onUpdateCheckoutDetails(checkout.keys.DELIVERY_SPEED, value);
    },
    [onUpdateCheckoutDetails],
  );

  const _onOrderTypeChanged = (value) => {
    onUpdateCheckoutDetails(checkout.keys.ORDER_TYPE, value);
  };

  const _onPaymentCardChanged = (name) => (value) => {
    const existingData = JSON.parse(
      JSON.stringify(checkoutData?.[checkout.keys.PAYMENT_METHOD]),
    );
    existingData[name] = value;
    onUpdateCheckoutDetails(checkout.keys.PAYMENT_METHOD, existingData);
  };

  const _onPaymentChanged = (value) => {
    onUpdateCheckoutDetails(checkout.keys.PAYMENT_METHOD, { method: value });
  };

  const _renderPromoCode = useCallback(
    () => (
      <PromoCode
        isQuotationNotReady={
          Boolean(deliveryQuotation.error) || deliveryQuotation.loading
        }
      />
    ),
    [deliveryQuotation.error, deliveryQuotation.loading],
  );

  const _renderPaymentOption = useCallback(
    () => (
      <Fragment>
        <PaymentOptions
          disabled={deliveryQuotation.loading}
          value={formPaymentOptions}
          onChange={_onPaymentOptionChanged}
          paymentMethods={checkoutInfo.delivery_payment_methods}
        />
        <DeliverySpeed
          enabled={!checkoutInfo.use_prio_speed_as_default}
          isLoading={deliveryQuotation.loading}
          isLalamove={deliveryQuotation.partner === 'lalamove'}
          value={formDeliverySpeed}
          onChange={_onDeliverySpeedChanged}
        />
      </Fragment>
    ),
    [
      checkoutInfo.delivery_payment_methods,
      deliveryQuotation.loading,
      formPaymentOptions,
      _onPaymentOptionChanged,
    ],
  );

  return (
    <Layout
      dataSet={{ media: ids.wrapper }}
      style={[
        styles.wrapper,
        !constants.isWeb && themeStyle.pageHorizontalSpacing,
      ]}
    >
      <View
        dataSet={{ media: platformStyleIds.webOnly }}
        style={platformStyles.webOnly}
      >
        {/* Basket note and store name */}
        <View style={themeStyle.spacingBottomMedium}>
          <Text category="p1" status="primary">
            Your {getCartTerm()} From
          </Text>
          <ListItem
            flex={false}
            title={checkoutInfo.store_name}
            rightIcon={{
              name: 'chevron-right',
              color: theme['color-success-500'],
            }}
            onPress={onBackToStore}
          />
        </View>
        {/* Accordions */}
        <Accordion iconName="shopping-cart-outline" title="Order Summary">
          <CheckoutItems />
        </Accordion>
        <Divider style={themeStyle.divider} />
        <Accordion iconName="pricetags-outline" title="Promo Code">
          {_renderPromoCode()}
        </Accordion>
        <Divider style={themeStyle.divider} />
        {isDelivery && (
          <Accordion
            iconName="file-text-outline"
            title="Delivery Payment Options"
          >
            {_renderPaymentOption()}
          </Accordion>
        )}
      </View>
      {/* Promo Code & Delivery Payment option Mobile */}
      <View
        dataSet={{ media: platformStyleIds.mobileAndTabletOnly }}
        style={platformStyles.mobileAndTabletOnly}
      >
        <View style={themeStyle.spacingBottomMedium}>{_renderPromoCode()}</View>
        {isDelivery && _renderPaymentOption()}
      </View>
      {/* Cost Summary Breakdown */}
      <CartSummary
        additionalCharge={additionalCharge}
        cart={checkoutItems}
        delivery={deliveryQuotation}
        hasPickupOrderType={
          checkoutInfo.order_types[pickupOrderType.apiDataKey]
        }
        isDelivery={isDelivery || isMealPlan}
        promotion={checkoutData[checkout.keys.PROMO_CODE]}
        onOrderTypeChange={_onOrderTypeChanged}
        noHspacing
      />
      {/* Payment Method */}
      <PaymentMethods
        value={checkoutData[checkout.keys.PAYMENT_METHOD]}
        onInputChange={_onPaymentCardChanged}
        onMethodChange={_onPaymentChanged}
      />
      {/* Place Order Button */}
      <ContinueToCheckoutButton
        additionalCharge={additionalCharge}
        cartItems={checkoutItems}
        isMealPlan={isMealPlan}
        deliveryQuotation={deliveryQuotation}
        mealPlanDiscount={mealPlanDiscount}
        placingOrder={isPlacingOrder}
        storeId={checkoutInfo.store_id}
        onContinueToCheckoutPress={onPlaceOrder}
      />
    </Layout>
  );
};

const { ids, styles } = StyleSheet.create({
  wrapper: {
    [`@media ${breakpoints.lg}`]: {
      ...themeStyle.pageVerticalSpacing,
      ...themeStyle.pageHorizontalSpacing,
      borderRadius: 24,
      marginTop: globalSpacing,
      backgroundColor: '#f7f9fc',
    },
  },
});

export default CheckoutRightContent;
